import { AsyncPipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  map,
  ReplaySubject,
  startWith
} from 'rxjs';
import { Delivery } from 'src/app/models';
import { Booking } from 'src/app/models/booking';
import { BookingItem } from 'src/app/models/booking-item';
import { RetailItemSale } from 'src/app/models/retail-item-sale';
import {
  Chargeable,
  PriceContainerV2Component
} from 'src/app/shared/price-container-v2/price-container-v2.component';
import { RetailItemStoreService } from 'src/app/shared/retail-item-list/retail-item.store.service';
import { AbstractPaymentService } from 'src/app/shared/services/abstract-payment.service';
import { BookingPaymentService } from 'src/app/shared/services/booking-payment.service';
import { DeliveryListItem } from 'src/app/shared/deliveries/docket-delivery-list/deliverable-service.interface';

@Component({
  selector: 'app-price-wrapper',
  standalone: true,
  imports: [PriceContainerV2Component, AsyncPipe],
  template: `
    @if (bookingId) {
      <app-price-container-v2
        [chargeableItems]="chargeables$ | async"
        [deliveryCharges]="deliveryCharges$ | async"
        paymentableType="booking"
        [paymentableId]="bookingId"
        [canAddPayments]="enabled"
      ></app-price-container-v2>
    }
  `,
  providers: [
    { provide: AbstractPaymentService, useClass: BookingPaymentService }
  ]
})
export class PriceWrapperComponent {
  @Input() set booking(booking: Booking) {
    this.bookingId = booking?.id;
    this.enabled = booking?.docket_id === null;
  }

  @Input() set bookingItems(items: BookingItem[]) {
    this.bookingItemChargeableSoruce.next(items);
  }

  @Input() set deliveries(deliveries: DeliveryListItem[]) {
    this.deliveryChargeSource.next(deliveries);
  }

  enabled = false;

  bookingId: number;

  private bookingItemChargeableSoruce = new ReplaySubject<BookingItem[]>(1);
  private deliveryChargeSource = new BehaviorSubject<DeliveryListItem[]>([]);

  deliveryCharges$ = this.deliveryChargeSource.pipe(
    map((deliveries) =>
      deliveries.map((delivery) => this.deliveryToChargeable(delivery))
    )
  );

  chargeables$ = combineLatest([
    this.retailItemStoreService.items$.pipe(
      map((items) => items.map((item) => this.retailItemToChargeable(item)))
    ),
    this.bookingItemChargeableSoruce.pipe(
      map((items) =>
        items
          .filter((item) => item.deleted_at === null)
          .map((item) => this.bookingItemToChargeable(item))
      )
    )
  ]).pipe(
    map(([retailItems, hireItems]) => [...retailItems, ...hireItems]),
    startWith([])
  );

  constructor(private retailItemStoreService: RetailItemStoreService) {}

  private bookingItemToChargeable(item: BookingItem): Chargeable {
    return {
      tax_rate: 0,
      charge: item.charge * 100
    };
  }

  private retailItemToChargeable({
    tax_rate,
    quantity,
    unit_price
  }: RetailItemSale): Chargeable {
    return {
      tax_rate,
      charge: quantity * unit_price
    };
  }

  private deliveryToChargeable(delivery: DeliveryListItem) {
    return {
      tax_rate: 0,
      charge: delivery.charge * 100
    };
  }
}
